"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../../utils/Api";

const ClientsTestimonial = () => {
  const { t } = useTranslation("home");
  const [current, setCurrent] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get('/review/public');
        //  console.log(response.data);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        } else {
          // Fallback fixed testimonials if no API data
          setTestimonials([
            { _id: 1, name: "Karim B.", role: "Développeur Full Stack", text: "Grâce à Tekhdem, j'ai décroché un poste qui correspond parfaitement à mes attentes en un temps record. La plateforme est intuitive et les offres sont de qualité !", rating: 5 },
            { _id: 2, name: "Amel M.", role: "Directrice RH", text: "Un outil indispensable pour nos recrutements. Nous avons trouvé des candidats qualifiés rapidement. L'interface recruteur est très bien pensée pour gérer les candidatures.", rating: 5 },
            { _id: 3, name: "Yacine B.", role: "Freelance Graphic Designer", text: "Je recommande vivement cette plateforme pour les freelances. J'ai pu trouver plusieurs missions intéressantes et élargir mon portefeuille client.", rating: 4 },
          ]);
        }
      } catch (error) {
        console.error("Error fetching reviews", error);
        // Fallback fixed testimonials on error
        setTestimonials([
          { _id: 1, name: "Karim B.", role: "Développeur Full Stack", text: "Grâce à Tekhdem, j'ai décroché un poste qui correspond parfaitement à mes attentes en un temps record. La plateforme est intuitive et les offres sont de qualité !", rating: 5 },
          { _id: 2, name: "Amel M.", role: "Directrice RH", text: "Un outil indispensable pour nos recrutements. Nous avons trouvé des candidats qualifiés rapidement. L'interface recruteur est très bien pensée pour gérer les candidatures.", rating: 5 },
          { _id: 3, name: "Yacine B.", role: "Freelance Graphic Designer", text: "Je recommande vivement cette plateforme pour les freelances. J'ai pu trouver plusieurs missions intéressantes et élargir mon portefeuille client.", rating: 4 },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const next = () => setCurrent((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  if (isLoading) {
    return null;
  }

  // Ensure rating is valid (1-5)
  const currentTestimonial = testimonials[current];
  const starCount = Math.min(Math.max(currentTestimonial.rating || 5, 1), 5);

  return (
    <section className="py-24 bg-gray-50 dark:bg-gray-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('testimonials.title')} <span className="text-yellow-500">{t('testimonials.titleHighlight')}</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400">{t('testimonials.subtitle')}</p>
        </div>

        <div className="max-w-4xl mx-auto relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 dark:border-gray-700 text-center relative"
            >
              <Quote className="absolute top-8 left-8 text-yellow-500/20 w-16 h-16" />

              <div className="flex justify-center gap-1 mb-6">
                {[...Array(starCount)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
              </div>

              <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-200 font-medium mb-8 leading-relaxed">
                "{testimonials[current].text}"
              </p>

              <div>
                <h4 className="font-bold text-gray-900 dark:text-white text-lg">{testimonials[current].name}</h4>
                <p className="text-yellow-500 text-sm">{testimonials[current].role}</p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          {testimonials.length > 1 && (
            <div className="flex justify-center gap-4 mt-8">
              <button aria-label="Précédent" onClick={prev} className="p-3 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-yellow-500 transition-colors">
                <ChevronLeft className="text-gray-600 dark:text-gray-300" />
              </button>
              <button aria-label="Suivant" onClick={next} className="p-3 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-yellow-500 transition-colors">
                <ChevronRight className="text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ClientsTestimonial;